import React, { useState, useEffect, useRef, useCallback } from 'react';
import VideoTimeline from './VideoTimeline';

const UnifiedVideoPlayer = ({
  videoType, // 'local' or 'youtube'
  videoUrl,
  youtubeUrl,
  currentTime,
  onTimeChange,
  onPlayStateChange,
  onSeek,
  dashboardId,
  telemetryData = [],
  style = {}
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [error, setError] = useState(null);
  
  const videoRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const timeUpdateIntervalRef = useRef(null);

  // Extract YouTube video ID
  const extractYouTubeVideoID = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Initialize YouTube player
  const initializeYouTubePlayer = useCallback(() => {
    if (!window.YT || !youtubeUrl) return;

    const videoId = extractYouTubeVideoID(youtubeUrl);
    if (!videoId) {
      setError('Invalid YouTube URL');
      return;
    }

    try {
      youtubePlayerRef.current = new window.YT.Player(`youtube-player-${dashboardId}`, {
        height: '100%',
        width: '100%',
        videoId: videoId,
        playerVars: {
          controls: 0, // Hide YouTube controls
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          fs: 0, // Disable fullscreen
          cc_load_policy: 0,
          start: 0
        },
        events: {
          onReady: (event) => {
            console.log('🎬 YouTube player ready');
            setPlayerReady(true);
            setDuration(event.target.getDuration());
            onPlayStateChange('ready');
          },
          onStateChange: (event) => {
            const state = event.data;
            console.log('🎬 YouTube state change:', state);
            
            if (state === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              onPlayStateChange('playing');
              startTimeTracking();
            } else if (state === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
              onPlayStateChange('paused');
              stopTimeTracking();
            } else if (state === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
              onPlayStateChange('ended');
              stopTimeTracking();
            }
          },
          onError: (event) => {
            console.error('🎬 YouTube player error:', event.data);
            setError('YouTube player error: ' + event.data);
          }
        }
      });
    } catch (error) {
      console.error('🎬 Failed to initialize YouTube player:', error);
      setError('Failed to initialize YouTube player');
    }
  }, [youtubeUrl, dashboardId, onPlayStateChange]);

  // Initialize local video
  const initializeLocalVideo = useCallback(() => {
    if (!videoRef.current || !videoUrl) return;

    const video = videoRef.current;
    
    const handleLoadedMetadata = () => {
      console.log('🎬 Local video loaded');
      setPlayerReady(true);
      setDuration(video.duration);
      onPlayStateChange('ready');
    };

    const handlePlay = () => {
      setIsPlaying(true);
      onPlayStateChange('playing');
      startTimeTracking();
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPlayStateChange('paused');
      stopTimeTracking();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onPlayStateChange('ended');
      stopTimeTracking();
    };

    const handleError = () => {
      setError('Failed to load video');
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl, onPlayStateChange]);

  // Start time tracking
  const startTimeTracking = useCallback(() => {
    if (timeUpdateIntervalRef.current) return;

    timeUpdateIntervalRef.current = setInterval(() => {
      let currentTime = 0;
      
      if (videoType === 'local' && videoRef.current) {
        currentTime = videoRef.current.currentTime;
      } else if (videoType === 'youtube' && youtubePlayerRef.current) {
        try {
          currentTime = youtubePlayerRef.current.getCurrentTime();
        } catch (error) {
          console.warn('Error getting YouTube time:', error);
          return;
        }
      }
      
      if (currentTime > 0) {
        onTimeChange(currentTime);
      }
    }, 100);
  }, [videoType, onTimeChange]);

  // Stop time tracking
  const stopTimeTracking = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  }, []);

  // Play/pause handler
  const handlePlayPause = useCallback(() => {
    if (!playerReady) return;

    if (videoType === 'local' && videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    } else if (videoType === 'youtube' && youtubePlayerRef.current) {
      if (isPlaying) {
        youtubePlayerRef.current.pauseVideo();
      } else {
        youtubePlayerRef.current.playVideo();
      }
    }
  }, [videoType, isPlaying, playerReady]);

  // Seek handler
  const handleSeek = useCallback((time) => {
    if (!playerReady) {
      console.log('🎬 Player not ready, skipping seek');
      return;
    }

    if (videoType === 'local' && videoRef.current) {
      videoRef.current.currentTime = time;
    } else if (videoType === 'youtube' && youtubePlayerRef.current) {
      // Check if YouTube player is ready and has seekTo method
      if (youtubePlayerRef.current.seekTo && typeof youtubePlayerRef.current.seekTo === 'function') {
        youtubePlayerRef.current.seekTo(time, true);
      } else {
        console.log('🎬 YouTube player not ready for seeking');
        return;
      }
    }
    
    // Call both onTimeChange and onSeek if provided
    onTimeChange(time);
    if (onSeek) {
      onSeek(time);
    }
  }, [videoType, playerReady, onTimeChange, onSeek]);

  // Initialize player based on type
  useEffect(() => {
    if (videoType === 'youtube') {
      // Load YouTube API if not already loaded
      if (!window.YT) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.onload = () => {
          window.onYouTubeIframeAPIReady = initializeYouTubePlayer;
          if (window.YT && window.YT.Player) {
            initializeYouTubePlayer();
          }
        };
        document.head.appendChild(script);
      } else {
        initializeYouTubePlayer();
      }
    } else if (videoType === 'local') {
      initializeLocalVideo();
    }

    return () => {
      stopTimeTracking();
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
      }
    };
  }, [videoType, initializeYouTubePlayer, initializeLocalVideo, stopTimeTracking]);

  // Handle external currentTime changes (for seeking from map/graph)
  useEffect(() => {
    if (currentTime > 0 && playerReady) {
      // Add a small delay to ensure YouTube player is fully ready
      const timeoutId = setTimeout(() => {
        handleSeek(currentTime);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentTime, playerReady, handleSeek]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimeTracking();
    };
  }, [stopTimeTracking]);

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        background: 'rgba(220,53,69,0.1)', 
        border: '1px solid #dc3545', 
        borderRadius: '8px',
        color: '#dc3545',
        ...style 
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      {/* Video Container */}
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        background: '#000',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {videoType === 'local' && (
          <video
            ref={videoRef}
            src={videoUrl}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
            preload="metadata"
          />
        )}
        
        {videoType === 'youtube' && (
          <div
            id={`youtube-player-${dashboardId}`}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        )}

        {/* Loading Overlay */}
        {!playerReady && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '16px'
          }}>
            Loading video...
          </div>
        )}
      </div>

      {/* Video Controls */}
      {playerReady && (
        <VideoTimeline
          currentTime={currentTime}
          duration={duration}
          onSeek={handleSeek}
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
          dashboardId={dashboardId}
          telemetryData={telemetryData}
          onTimeChange={onTimeChange}
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            right: '10px'
          }}
        />
      )}
    </div>
  );
};

export default UnifiedVideoPlayer;
